import { useNavigate } from "react-router-dom";
import { Crosshair, Trophy, Timer } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Feedback } from "@/components/Feedback";
import { scoreItem } from "@/lib/scoring";
import { getBank, getHistory, saveDrill } from "@/lib/bank";
import { buildDrill, weakParts } from "@/lib/drills";

export const WeakSpotCard = () => {
  const nav = useNavigate();
  const hist = getHistory();
  const weak = weakParts(hist);
  const start = (timed) => { saveDrill(buildDrill(hist, getBank(), timed)); nav("/practice?drill=1"); };
  return (
    <div data-testid="weak-spot-card" className="rounded-xl bg-white p-6 shadow-xl shadow-slate-950/30">
      <div className="flex items-center gap-2"><Crosshair className="h-5 w-5 text-indigo-600" /><h3 className="font-display text-xl font-bold text-slate-900">Weak Spot Drills</h3></div>
      {weak.length === 0 ? (
        <p data-testid="weak-spot-empty" className="mt-2 text-sm text-slate-500">Complete a mock exam to unlock a drill built from your lowest-scoring parts.</p>
      ) : (
        <>
          <p className="mt-1 text-sm text-slate-500">Your lowest parts across your last {Math.min(hist.length, 3)} exam(s). Fresh items you haven't seen yet.</p>
          <div className="mt-4 space-y-2">
            {weak.map((w) => (
              <div key={w.key} data-testid={`weak-part-${w.key}`} className="flex items-center gap-3">
                <span className="w-44 truncate text-sm text-slate-700"><b className="font-mono text-indigo-600">{w.key}</b> · {w.name}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-rose-500" style={{ width: `${Math.round(w.avg * 100)}%` }} /></div>
                <span className="w-10 text-right font-mono text-xs text-slate-600">{Math.round(w.avg * 100)}%</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button data-testid="start-weak-drill-btn" onClick={() => start(false)} className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700">Start drill</button>
            <button data-testid="start-timed-drill-btn" onClick={() => start(true)} className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-900 px-5 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"><Timer className="h-4 w-4" />Timed drill (exam conditions)</button>
          </div>
        </>
      )}
    </div>
  );
};

export const DrillBanner = ({ drill, onExit }) => (
  <div data-testid="drill-banner" className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-4 py-3">
    <Crosshair className="h-4 w-4 text-indigo-300" />
    <span className="text-sm font-semibold text-white">Weak Spot Drill · Parts {drill.weak.map((w) => w.key).join(", ")}</span>
    {drill.timed && <span data-testid="drill-timed-badge" className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold text-slate-900"><Timer className="h-3 w-3" />Timed · no replay</span>}
    <span data-testid="drill-progress" className="font-mono text-xs text-indigo-200">Item {Math.min(drill.idx + 1, drill.queue.length)} of {drill.queue.length}</span>
    <button data-testid="drill-exit-btn" onClick={onExit} className="ml-auto text-xs font-semibold text-slate-300 underline hover:text-white">Exit drill</button>
  </div>
);

export const DrillSummary = ({ drill, onFinish }) => (
  <div data-testid="drill-summary" className="space-y-5">
    <div className="flex items-center gap-2"><Trophy className="h-6 w-6 text-amber-500" /><h3 className="font-display text-2xl font-bold text-slate-900">Drill complete</h3></div>
    <div className="space-y-2">
      {drill.weak.map((w) => {
        const s = drill.queue.map((q, i) => (q.part === w.key ? drill.scores[i] : null)).filter((v) => v != null);
        const now = s.length ? s.reduce((a, b) => a + b, 0) / s.length : 0;
        return (
          <div key={w.key} data-testid={`drill-result-${w.key}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm">
            <span className="text-slate-700"><b className="font-mono text-indigo-600">{w.key}</b> · {w.name}</span>
            <span className="font-mono text-xs"><span className="text-slate-400">exam {Math.round(w.avg * 100)}%</span> → <b className={now >= w.avg ? "text-emerald-600" : "text-rose-600"}>drill {Math.round(now * 100)}%</b></span>
          </div>
        );
      })}
    </div>
    {drill.timed && (
      <div className="rounded-lg border border-slate-200 p-2" data-testid="drill-review-list">
        <Accordion type="single" collapsible>
          {drill.queue.map((q, i) => (
            <AccordionItem key={i} value={String(i)}>
              <AccordionTrigger data-testid={`drill-review-item-${i}`} className="px-2 text-left text-sm hover:no-underline">
                <span className="flex w-full items-center gap-3 pr-3"><span className="rounded bg-indigo-50 px-2 py-0.5 font-mono text-xs font-bold text-indigo-700">{q.part}</span>
                  <span className="flex-1 truncate text-slate-700">{q.item.text || q.item.sentence || q.item.answer || q.item.question}</span>
                  <span className="font-mono text-xs font-bold text-slate-600">{Math.round((drill.scores[i] || 0) * 100)}%</span></span>
              </AccordionTrigger>
              <AccordionContent className="px-2"><Feedback partKey={q.part} item={q.item} response={drill.responses?.[i] || {}} result={scoreItem(q.part, q.item, drill.responses?.[i] || {})} /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    )}
    <button data-testid="drill-finish-btn" onClick={onFinish} className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">Finish drill</button>
  </div>
);
